import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";

const aboutLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于洞洞杂货铺世界观解析站",
  description: "洞洞杂货铺世界观解析站的品牌介绍、定位、发展理念、内容原则与用户口碑。",
};

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://storiha.github.io/" },
    { "@type": "ListItem", position: 2, name: "关于我们", item: "https://storiha.github.io/about" },
  ],
};

const values = [
  {
    icon: "ri-article-line",
    title: "把散落的线索串起来",
    desc: "作品里那些一闪而过的小细节，往往藏着最重要的信息。我们把它们整理成清晰、可检索的内容，帮你不再错过关键伏笔。",
  },
  {
    icon: "ri-eye-line",
    title: "不同视角，都值得被看到",
    desc: "考据没有唯一答案。我们尊重每一位读者对设定的理解，欢迎不同的解读与讨论，共同把世界观的拼图补完整。",
  },
  {
    icon: "ri-heart-line",
    title: "有温度的科普",
    desc: "我们不堆砌术语，而是用自然、好懂的语言把深邃的设定讲明白，让新读者也能轻松入坑，老读者也能有新发现。",
  },
];

const testimonials = [
  {
    name: "追更三年的考据党",
    content: "整理得太清楚了！看完世界观解析那篇，之前没想通的伏笔一下就连起来了。",
  },
  {
    name: "刚入坑的新读者",
    content: "从一个链接点进来，本来只想随便看看，结果把设定百科全翻完了，成功入坑。",
  },
  {
    name: "同好群群友",
    content: "每次更新都能第一时间看到新设定整理，省了我自己反复翻漫画的功夫，太爱了。",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="关于我们 - 洞洞杂货铺世界观解析站"
        description="洞洞杂货铺世界观解析站：专注洞洞杂货铺漫画的设定考据与世界观解析，秉持把散落伏笔串成清晰脉络的内容理念，欢迎每一位读者一起深挖隐藏线索。"
        keywords="洞洞杂货铺解析,洞洞杂货铺考据,洞洞杂货铺设定站,洞洞杂货铺关于我们"
        canonicalPath="/about"
        jsonLd={[aboutLd, breadcrumbLd]}
      />
      <Navbar />
      <main>
        <div className="mx-auto w-full max-w-6xl px-4 pt-8 md:px-6">
          <Breadcrumb items={[{ label: "关于我们" }]} />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800 whitespace-nowrap">
              关于我们
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              我们为什么做这个站
            </h1>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              喜欢《洞洞杂货铺》的人，大概都有过同一种经历：明明看完了，却总觉得还有哪里没懂——
              那个洞洞到底是什么意思？那家店为什么永远走不到尽头？那个反复出现的瓶子又是什么？
              于是我们这群读者凑在一起，决定把这些困惑变成清晰的答案。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              这就是「洞洞杂货铺世界观解析站」的起点：我们不生产故事，只负责把故事里
              那些藏得深的设定、伏笔与世界观脉络，认真整理出来，陪更多读者一起读懂它。
            </p>
          </div>

          <div className="mt-12 rounded-2xl bg-background-100/70 p-6 ring-1 ring-background-200/70 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              我们的定位与理念
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              我们把自己定位为「洞洞杂货铺的读者服务站」：面向所有喜欢这部作品、想要深入
              理解其世界观与故事背景的人。无论是刚入坑的新读者，还是追更多年的老读者，
              都能在这里找到对你有帮助的内容。我们相信，一部好作品的深度，值得被认真对待。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {values.map((v) => (
                <div key={v.title} className="rounded-xl bg-background-50 p-5 ring-1 ring-background-200/70">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                    <i className={`${v.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-700">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-accent-100/50 p-6 ring-1 ring-accent-200/60 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              我们的内容原则
            </h2>
            <ul className="mt-4 space-y-3 text-sm leading-7 text-foreground-700">
              {[
                "所有解析均基于漫画实际内容整理，不做脱离文本的过度脑补",
                "涉及考据与推测时，会明确区分「已知设定」与「读者猜想」",
                "坚持用简体中文、自然平实的语言写作，避免术语堆砌",
                "内容随漫画更新持续补充，保持设定百科的完整与准确",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line mt-1 text-accent-600"></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              来自读者的声音
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-2xl bg-background-100/70 p-5 ring-1 ring-background-200/70">
                  <div className="flex gap-1 text-secondary-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-6 text-foreground-700">
                    「{t.content}」
                  </blockquote>
                  <figcaption className="mt-4 text-xs font-medium text-foreground-800">
                    —— {t.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-primary-100/50 p-6 ring-1 ring-primary-200/60 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              如果你发现了我们遗漏的隐藏线索，或对某处设定有自己的独到理解，
              都欢迎与我们分享。你可以通过
              <a href="/contact" className="mx-1 font-medium text-primary-700 underline underline-offset-4">
                联系我们
              </a>
              页面提交留言，也可以发送邮件到 hello@dongdong.example.com。
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}